import React, { useState } from "react";
import { Alert, Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useGetSyncMetricsQuery, useGetSyncResultsQuery, useGetSyncSchedulesQuery } from "state/rtkQuery/sync/syncQueries";

import { PAGE_REFRESH, PAGE_REFRESH_MS, recordAsTask } from "./historyFilters";
import MetricsPanel, { LOOKBACKS, percent, readTotals } from "./MetricsPanel";
import { getAction } from "./syncActions";
import { formatDateTime, isActiveState, parseBackendTime, scheduleWhen, timeAgo } from "./syncFormat";
import { CallerCell, headCellSx, HistoryStatusPill, LiveDot, mono, OutcomePill, Panel, StatePill, TaskName } from "./SyncParts";
import { useSyncTasks } from "./SyncTasksContext";
import { taskNameFilter, useSyncTaskNames } from "./useSyncTaskNames";
import VerifyOrgPanel from "./VerifyOrgPanel";

// A tile. When `onOpen` is given it is a button that goes to the runs behind the number.
dayjs.extend(utc);

const Kpi = ({ label, value, note, color, testId, onOpen, openLabel, live = false }) => (
  <Box
    sx={{
      background: "#fff",
      border: "1px solid #e2e8f0",
      borderTop: `3px solid ${color}`,
      borderRadius: "14px",
      p: "16px 18px",
      ...(onOpen && { cursor: "pointer", transition: "box-shadow .15s, border-color .15s", "&:hover, &:focus-visible": { boxShadow: "0 8px 22px rgba(15,23,42,.08)", borderColor: "#cbd5e1", outline: "none" } }),
    }}
    data-testid={testId}
    {...(onOpen && {
      role: "button",
      tabIndex: 0,
      "aria-label": `${label}: ${value}. ${openLabel}`,
      onClick: onOpen,
      onKeyDown: (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      },
    })}
  >
    <Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</Typography>
    <Typography sx={{ fontSize: 28, fontWeight: 700, my: 0.5 }}>{value}</Typography>
    <Typography sx={{ color: live ? "#0969da" : "#64748b", fontSize: 12, fontWeight: live ? 600 : 400 }} data-testid={live ? `${testId}-live` : undefined}>
      {live && <LiveDot />}
      {note}
    </Typography>
    {onOpen && <Typography sx={{ color: "#0969da", fontSize: 12, fontWeight: 600, mt: 0.5 }}>{openLabel} →</Typography>}
  </Box>
);

const QUICK_ACTIONS = ["verify-pending-all", "metered-usage", "copilot-org-usage"];

const OverviewTab = ({ onRun, onGo, onOpenTask, onOpenRun, includeSystem = false }) => {
  const { tasks } = useSyncTasks();
  const [lookbackHours, setLookbackHours] = useState(24);
  const { data, isError } = useGetSyncSchedulesQuery({ enabled: "true" }, PAGE_REFRESH);
  const schedules = data?.items || [];
  // Housekeeping runs (recovery.*, beat.*) would swamp the numbers and the list, so they are left out unless asked for.
  const { userNames, ready: namesReady } = useSyncTaskNames();
  const taskNames = taskNameFilter({ includeSystem, userNames });
  // Everything here refreshes itself every 30 seconds while you are looking at the page (paused when the window is not in
  // focus), so the numbers, recent runs and schedule count never go stale.
  const { data: recent, isError: historyUnavailable } = useGetSyncResultsQuery(
    { limit: 6, task_name: taskNames },
    { skip: !includeSystem && !namesReady, ...PAGE_REFRESH }
  );

  const { data: metrics, isError: metricsUnavailable } = useGetSyncMetricsQuery(
    { lookback_hours: lookbackHours, task_name: taskNames },
    { skip: !includeSystem && !namesReady, ...PAGE_REFRESH }
  );

  const totals = readTotals(metrics);
  // Runs that were submitted in the window and haven't finished yet (waiting, running or retrying).
  const stillGoing = totals.started + totals.submitted + totals.retry;
  const windowLabel = LOOKBACKS.find((option) => option.hours === lookbackHours)?.label.toLowerCase() || "";
  const recentRuns = recent?.results || [];

  // These local tiles are the fallback while run metrics aren't available.
  const running = tasks.filter((task) => isActiveState(task.state)).length;
  const finished = tasks.filter((task) => task.state === "SUCCESS").length;
  const failed = tasks.filter((task) => task.state === "FAILURE").length;

  // The runs behind a number: Run history filtered the same way as the tile (same window, same system-task setting).
  // The history filter works in whole days, so it starts at the day the window began.
  const windowStart = dayjs.utc().subtract(lookbackHours, "hour").format("YYYY-MM-DD");
  const openRuns = (statuses) => () => onGo("history", { h_status: statuses.join(","), h_from: windowStart, ...(includeSystem ? { h_system: "1" } : {}) });

  const recentSchedules = [...schedules]
    .filter((schedule) => schedule.last_run_at)
    .sort((a, b) => (parseBackendTime(b.last_run_at)?.valueOf() ?? 0) - (parseBackendTime(a.last_run_at)?.valueOf() ?? 0))
    .slice(0, 4);

  return (
    <>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" }, gap: 2, mb: 2.5 }}>
        <Kpi testId="kpi-schedules" label="Enabled schedules" value={isError ? "—" : (data?.total ?? 0)} note="paused ones aren't counted" color="#86BC25" onOpen={() => onGo("schedules")} openLabel="View schedules" />
        {metricsUnavailable ? (
          <>
            <Kpi testId="kpi-running" label="Running now" value={running} note="started from this browser" color="#0969da" live={running > 0} onOpen={() => onGo("tasks")} openLabel="View task tracker" />
            <Kpi testId="kpi-finished" label="Finished" value={finished} note="this session" color="#4d7a10" onOpen={() => onGo("tasks")} openLabel="View task tracker" />
            <Kpi testId="kpi-failed" label="Failed" value={failed} note={failed ? "needs a look" : "none this session"} color="#E2231A" onOpen={() => onGo("tasks")} openLabel="View task tracker" />
          </>
        ) : (
          <>
            <Kpi testId="kpi-finished" label="Succeeded" value={totals.success} note={`${windowLabel} · worker finished`} color="#4d7a10" onOpen={openRuns(["success"])} openLabel="View succeeded runs" />
            <Kpi testId="kpi-failed" label="Failed" value={totals.failure} note={totals.failure ? "needs a look" : windowLabel} color="#E2231A" onOpen={openRuns(["failure"])} openLabel="View failed runs" />
            <Kpi testId="kpi-rate" label="Success rate" value={percent(totals.rate)} note={stillGoing ? `${stillGoing} still running` : "of finished runs"} color="#0969da" live={stillGoing > 0} onOpen={openRuns(["success", "failure"])} openLabel="View finished runs" />
          </>
        )}
      </Box>

      {metricsUnavailable && (
        <Alert severity="info" sx={{ mb: 2.5 }} icon={false} data-testid="overview-fallback">
          <b>Run history isn't available here yet.</b> The run numbers above are for actions started from this browser in this session. The schedules count covers <b>enabled</b> schedules only.
        </Alert>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.6fr 1fr" }, gap: 2 }}>
        <Panel title="Recent runs" subtitle={historyUnavailable ? "submitted from this browser" : "latest retained runs"} actions={<Button size="small" variant="outlined" onClick={() => onGo(historyUnavailable ? "tasks" : "history")} sx={{ textTransform: "none" }}>{historyUnavailable ? "Open task tracker →" : "Open run history →"}</Button>}>
          {!historyUnavailable && recentRuns.length > 0 ? (
            <Table size="small">
              <TableHead>
                <TableRow>{["Task", "Status", "Outcome", "Triggered by", "Submitted"].map((label) => <TableCell key={label} sx={headCellSx}>{label}</TableCell>)}</TableRow>
              </TableHead>
              <TableBody>
                {recentRuns.map((record) => (
                  <TableRow key={record.task_id} hover sx={{ cursor: "pointer" }} onClick={() => onOpenRun(record.task_id)} data-testid="recent-run">
                    <TableCell><TaskName name={record.task_name} /></TableCell>
                    <TableCell><HistoryStatusPill status={record.status} /></TableCell>
                    <TableCell><OutcomePill task={recordAsTask(record)} /></TableCell>
                    <TableCell><CallerCell source={record} /></TableCell>
                    <TableCell sx={{ color: "#64748b", fontSize: 12.5 }}>{timeAgo(record.submitted_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : tasks.length === 0 || !historyUnavailable ? (
            <Typography sx={{ p: 4, textAlign: "center", color: "#94a3b8" }}>{historyUnavailable ? "Nothing run from this browser yet." : "No runs are retained yet."}</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>{["Action", "Scope", "Status", "Outcome", "Started"].map((label) => <TableCell key={label} sx={headCellSx}>{label}</TableCell>)}</TableRow>
              </TableHead>
              <TableBody>
                {tasks.slice(0, 6).map((task) => (
                  <TableRow key={task.id} hover sx={{ cursor: "pointer" }} onClick={() => onOpenTask(task.id)} data-testid="recent-run">
                    <TableCell><b>{task.title}</b><Typography sx={mono}>{task.taskName}</Typography></TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>{task.scope}</TableCell>
                    <TableCell><StatePill state={task.state} /></TableCell>
                    <TableCell><OutcomePill task={task} /></TableCell>
                    <TableCell sx={{ color: "#64748b", fontSize: 12.5 }}>{timeAgo(task.startedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Panel>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Panel title="Quick actions">
            <Box sx={{ p: "12px 18px", display: "flex", flexDirection: "column", gap: 1.25 }}>
              {QUICK_ACTIONS.map((id) => {
                const action = getAction(id);
                return (
                  <Box key={id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5 }}>
                    <Box><Typography sx={{ fontWeight: 600, fontSize: 13 }}>{action.title}</Typography><Typography sx={{ color: "#64748b", fontSize: 12 }}>{action.scope}</Typography></Box>
                    <Button size="small" variant="contained" onClick={() => onRun(id)} aria-label={`Quick run ${action.title}`} sx={{ background: "#86BC25", textTransform: "none", fontWeight: 700, boxShadow: "none", "&:hover": { background: "#6a9a1e", boxShadow: "none" } }}>Run</Button>
                  </Box>
                );
              })}
            </Box>
          </Panel>
          <Panel title="Recently run schedules" actions={<Button size="small" variant="outlined" onClick={() => onGo("schedules")} sx={{ textTransform: "none" }}>All →</Button>}>
            <Box sx={{ p: "8px 14px 12px" }}>
              {recentSchedules.length === 0 ? (
                <Typography sx={{ color: "#94a3b8", fontSize: 13, p: 1 }}>{isError ? "Couldn't load the schedules." : "No schedule has run yet."}</Typography>
              ) : (
                recentSchedules.map((schedule) => (
                  <Box key={schedule.name} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: "8px 6px" }}>
                    <Box><Typography sx={{ fontWeight: 600, fontSize: 13 }}>{schedule.name}</Typography><Typography sx={{ color: "#64748b", fontSize: 12 }}>{scheduleWhen(schedule)}</Typography></Box>
                    <Typography sx={{ color: "#64748b", fontSize: 12 }} title={formatDateTime(schedule.last_run_at)}>{timeAgo(schedule.last_run_at)}</Typography>
                  </Box>
                ))
              )}
            </Box>
          </Panel>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <MetricsPanel lookbackHours={lookbackHours} onLookbackChange={setLookbackHours} taskNames={taskNames} refreshMs={PAGE_REFRESH_MS} />
      </Box>

      <VerifyOrgPanel />
    </>
  );
};

export default OverviewTab;
